import { DialogsRenderer } from '../components/widgets/dialog';
import { NotificationToasterRenderer } from '../components/widgets/notificationToaster';
import React, { FC, FunctionComponent, Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { GetIcon } from 'src/components/widgets/leftAppSidebar/IconManager';
import {
    APP_SIDEBAR_WIDTH,
    Body,
    ContainerIcon,
    ContainerLeft,
    ContainerLeftBottom,
    ContainerLeftTop,
    Header,
    HeaderHomeLink,
    HeaderLogoLink,
    LinkSidebar,
    Main,
    MenuDivider,
    Navbar,
    NavbarContainer,
    NavbarLi
} from 'src/components/widgets/leftAppSidebar/leftAppSidebarStyles';
import { ItemMenu } from 'src/components/widgets/leftAppSidebar/Menu';
import { Logout } from 'src/shared/logout';
import { useRelayStore } from 'src/relay';
import { backofficeUrl, getPathName, T } from 'src/shared/helpers';
import SignOutIcon from 'src/shared/material_icons/action/logout.svg';
import LogoZakeke from 'src/shared/svg/logoZakeke';
import styled, { ThemeProvider } from 'styled-components';
import ErrorPageBoundary from '../components/pages/_error';
import OverlayLoading from '../components/widgets/overlayLoading';
import { AppStateProvider } from '../shared/appState';
import { scrollbarsStyle } from '../shared/globals';
import theme from '../shared/theme';
import { administratorLayoutContainerQuery } from './__generated__/administratorLayoutContainerQuery.graphql';

const Container = styled.div`
    font-size: 14px;
    font-family: ${(props) => props.theme.app.font};
    color: ${(props) => props.theme.app.primary};
    min-height: 100%;
    padding: 0px;

    ${scrollbarsStyle}
`;

const AdminShell = styled.div`
    min-height: 100vh;
`;

const AdminContent = styled.div`
    min-height: 100vh;
    margin-left: ${APP_SIDEBAR_WIDTH}px;
    padding: 0 48px 55px;
    background-color: #f9f9f9;
    display: flex;
    flex-direction: column;
`;

const ScrollTop: FC<{ children?: React.ReactNode }> = ({ children }) => {
    return <React.Fragment>{children}</React.Fragment>;
};

const AdminSelectedMenuItem = styled.span`
    color: ${(props) => props.theme.app.secondary};
`;

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const query = useLazyLoadQuery<administratorLayoutContainerQuery>(
        graphql`
            query administratorLayoutContainerQuery {
                treeMenu
            }
        `,
        {}
    );

    const rootMenu = JSON.parse(query.treeMenu) as ItemMenu;
    const { invalidateRelayStore } = useRelayStore();
    const adminHomeUrl = backofficeUrl('/console');

    const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        navigate(adminHomeUrl);
    };

    const isMenuItemSelected = (item: ItemMenu) => {
        const currentPath = `${location.pathname}${location.search}`.toLowerCase().replace(/\/$/, '');
        const itemPath = getPathName(item.urlPage).toLowerCase().replace(/\/$/, '');

        return currentPath === itemPath || (!!item.regex && new RegExp(item.regex, 'i').test(currentPath));
    };

    const handleMenuItemClick = (item: ItemMenu, e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        if (item.isExternalUrl) {
            window.open(item.urlPage, '_blank');
            return;
        }

        navigate(getPathName(item.urlPage));
    };

    const menuItems = rootMenu.menu.filter((item) => item.name.toLowerCase() !== 'home');

    return (
        <Main id='main-left-sidebar'>
            <Header>
                <HeaderLogoLink href={adminHomeUrl} onClick={handleHomeClick}>
                    <LogoZakeke />
                </HeaderLogoLink>
                <HeaderHomeLink href={adminHomeUrl} title={T._('Home', 'Admin')} onClick={handleHomeClick}>
                    {GetIcon('home_solid') && React.createElement(GetIcon('home_solid')!)}
                </HeaderHomeLink>
            </Header>

            <Body>
                <ContainerLeft>
                    <ContainerLeftTop>
                        <NavbarContainer>
                            <Navbar style={{ position: 'relative' }}>
                                {menuItems.map((item) => {
                                    const isSelected = isMenuItemSelected(item);
                                    return (
                                        <NavbarLi key={item.urlPage} $isVisible>
                                            <LinkSidebar
                                                href={item.urlPage}
                                                onClick={(e) => handleMenuItemClick(item, e)}
                                                target={item.isExternalUrl ? '_blank' : '_self'}
                                            >
                                                <ContainerIcon $isSelected={isSelected}>
                                                    {GetIcon(item.icon) && React.createElement(GetIcon(item.icon)!)}
                                                </ContainerIcon>
                                                {isSelected ? <AdminSelectedMenuItem>{item.name}</AdminSelectedMenuItem> : <span>{item.name}</span>}
                                            </LinkSidebar>
                                        </NavbarLi>
                                    );
                                })}
                            </Navbar>
                        </NavbarContainer>
                    </ContainerLeftTop>

                    <ContainerLeftBottom>
                        <MenuDivider />
                        <NavbarLi $isVisible>
                            <LinkSidebar
                                href='javascript:void(0)'
                                onClick={(e) => {
                                    e.preventDefault();
                                    Logout({ invalidateRelayStore });
                                }}
                            >
                                <ContainerIcon>
                                    <SignOutIcon />
                                </ContainerIcon>
                                <span>{T._('Logout', 'Admin')}</span>
                            </LinkSidebar>
                        </NavbarLi>
                    </ContainerLeftBottom>
                </ContainerLeft>
            </Body>
        </Main>
    );
};

export const AdmininistratorLayoutContainer: FunctionComponent<{ className?: string; children?: React.ReactNode }> = ({ className, children }) => {
    return (
        <AppStateProvider>
            <ThemeProvider theme={theme}>
                <NotificationToasterRenderer />
                <DialogsRenderer />
                <OverlayLoading />
                <ScrollTop>
                    <Router>
                        <Container className={className}>
                            <AdminShell>
                                <Sidebar />
                                <AdminContent>
                                    <ErrorPageBoundary>
                                        <Suspense fallback={<OverlayLoading forceShow />}>{children}</Suspense>
                                    </ErrorPageBoundary>
                                </AdminContent>
                            </AdminShell>
                        </Container>
                    </Router>
                </ScrollTop>
            </ThemeProvider>
        </AppStateProvider>
    );
};
